import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { Bar, BarChart, CartesianGrid, XAxis } from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '../ui/chart';

const InteractiveBarChart = ({
    title,
    description,
    data,
    dataKeys = [], // Array of { key: 'totalBookings', label: 'Bookings', color: 'hsl(...)' }
    height = 300,
    trendData = null, // Trend data object with { percentage, currentTotal, previousTotal, currentPeriod, previousPeriod }
    stacked = false, // If true, show stacked bars instead of switching between metrics
    grouped = false, // If true, render all dataKeys as side-by-side bars (no metric switcher, no stack)
    invertTrend = false, // If true, invert trend colors (green for decrease, red for increase)
    isCurrency = false, // If true, format tooltip values as currency
    valueSuffix = '' // Optional suffix appended to tooltip / axis values (e.g. '%')
}) => {
    const [activeChart, setActiveChart] = useState(dataKeys[0]?.key || '');
    const [showTooltip, setShowTooltip] = useState(false);
    const [activeIndex, setActiveIndex] = useState(null);
    const [isMobile, setIsMobile] = useState(false);
    const chartContainerRef = useRef(null);
    
    // Extract trend percentage for backward compatibility
    const trendPercentage = trendData?.percentage ?? null;

    // Calculate totals for each metric
    const totals = useMemo(() => {
        const result = {};
        dataKeys.forEach(({ key }) => {
            result[key] = data.reduce((acc, curr) => acc + (curr[key] || 0), 0);
        });
        return result;
    }, [data, dataKeys]);

    // Detect if device is mobile
    useEffect(() => {
        const checkIfMobile = () => {
            setIsMobile(window.innerWidth < 768 || 'ontouchstart' in window);
        };
        
        checkIfMobile();
        window.addEventListener('resize', checkIfMobile);
        
        return () => window.removeEventListener('resize', checkIfMobile);
    }, []);

    // Convert touch events to mouse events for Recharts on mobile
    const handleTouchStart = useCallback((e) => {
        if (!isMobile || !chartContainerRef.current) return;
        
        const touch = e.touches[0];
        const target = e.target;
        
        // Create and dispatch a simulated mouseenter event
        const mouseEvent = new MouseEvent('mouseenter', {
            bubbles: true,
            cancelable: true,
            view: window,
            clientX: touch.clientX,
            clientY: touch.clientY
        });
        target.dispatchEvent(mouseEvent);
        
        // Also dispatch mousemove to trigger tooltip
        const mouseMoveEvent = new MouseEvent('mousemove', {
            bubbles: true,
            cancelable: true,
            view: window,
            clientX: touch.clientX,
            clientY: touch.clientY
        });
        target.dispatchEvent(mouseMoveEvent);
    }, [isMobile]);
    
    const handleTouchEnd = useCallback((e) => {
        if (!isMobile) return;
        
        // Dispatch mouseleave event
        const target = e.target;
        const mouseEvent = new MouseEvent('mouseleave', {
            bubbles: true,
            cancelable: true,
            view: window
        });
        target.dispatchEvent(mouseEvent);
    }, [isMobile]);

    // Handle click outside to close tooltip on mobile
    useEffect(() => {
        if (!isMobile) return;

        const handleClickOutside = (event) => {
            // Close tooltip if clicking outside the chart
            const chartContainer = event.target.closest('.recharts-wrapper');
            if (!chartContainer && chartContainerRef.current) {
                setActiveIndex(null);
            }
        };

        document.addEventListener('touchstart', handleClickOutside);
        document.addEventListener('click', handleClickOutside);

        return () => {
            document.removeEventListener('touchstart', handleClickOutside);
            document.removeEventListener('click', handleClickOutside);
        };
    }, [isMobile]);

    if (!data || data.length === 0) {
        return (
            <Card style={{ paddingTop: 0, paddingBottom: 0 }}>
                <CardHeader
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        borderBottom: '1px solid hsl(var(--border))',
                        padding: 0
                    }}
                >
                    <div style={{
                        display: 'flex',
                        flex: 1,
                        flexDirection: 'column',
                        justifyContent: 'center',
                        gap: '0.25rem',
                        padding: '1.5rem 1.5rem 0.75rem 1.5rem'
                    }}>
                        <CardTitle style={{ fontSize: '1.125rem', fontWeight: 600 }}>{title}</CardTitle>
                        {description && <CardDescription style={{ fontSize: '0.875rem', color: '#6b7280' }}>{description}</CardDescription>}
                    </div>
                    {trendPercentage !== null && (() => {
                        // Invert logic if invertTrend is true (for metrics where increase is bad)
                        const isPositive = trendPercentage >= 0;
                        const shouldShowPositive = invertTrend ? !isPositive : isPositive;
                        const color = shouldShowPositive ? '#16a34a' : '#dc2626';
                        const bgColor = shouldShowPositive ? 'rgba(22, 163, 74, 0.1)' : 'rgba(220, 38, 38, 0.1)';
                        const arrow = shouldShowPositive ? '↑' : '↓';

                        return (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                marginLeft: '1rem',
                                flexShrink: 0,
                                padding: '1.5rem 1.5rem 0.75rem 0'
                            }}>
                                <div style={{
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                    color: color,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.25rem',
                                    padding: '0.25rem 0.5rem',
                                    backgroundColor: bgColor,
                                    borderRadius: '0.375rem'
                                }}>
                                    <span>{arrow}</span>
                                    <span>
                                        {Math.abs(trendPercentage).toFixed(1)}%
                                    </span>
                                </div>
                            </div>
                        );
                    })()}
                </CardHeader>
                <CardContent style={{ padding: '1rem 1.5rem 1.5rem 1.5rem' }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: `${height}px`,
                        color: '#6b7280'
                    }}>
                        No data available
                    </div>
                </CardContent>
            </Card>
        );
    }

    const chartConfig = dataKeys.reduce((config, { key, label, color }) => {
        config[key] = {
            label,
            color: color || 'hsl(222.2, 47.4%, 11.2%)',
        };
        return config;
    }, {});

    return (
        <Card style={{ paddingTop: 0 }}>
            <CardHeader
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    borderBottom: '1px solid hsl(var(--border))',
                    padding: 0
                }}
            >
                <div style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    padding: '1.5rem 1.5rem 0.75rem 1.5rem'
                }}>
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        gap: '0.25rem',
                        flex: 1,
                        minWidth: 0
                    }}>
                        <CardTitle style={{ fontSize: '1.125rem', fontWeight: 600 }}>{title}</CardTitle>
                        {description && (
                            <CardDescription style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                                {description}
                            </CardDescription>
                        )}
                    </div>
                    {trendPercentage !== null && trendData && (() => {
                        // Invert logic if invertTrend is true (for metrics where increase is bad)
                        const isPositive = trendPercentage >= 0;
                        const shouldShowPositive = invertTrend ? !isPositive : isPositive;
                        const color = shouldShowPositive ? '#16a34a' : '#dc2626';
                        const bgColor = shouldShowPositive ? 'rgba(22, 163, 74, 0.1)' : 'rgba(220, 38, 38, 0.1)';
                        const arrow = isPositive ? '↑' : '↓';
                        
                        const formatValue = (value) => {
                            if (isCurrency) {
                                return `AED ${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                            }
                            return value.toLocaleString();
                        };

                        return (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                marginLeft: '1rem',
                                flexShrink: 0,
                                position: 'relative'
                            }}>
                                <div 
                                    style={{
                                        fontSize: '0.875rem',
                                        fontWeight: 600,
                                        color: color,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.25rem',
                                        padding: '0.25rem 0.5rem',
                                        backgroundColor: bgColor,
                                        borderRadius: '0.375rem',
                                        cursor: 'pointer',
                                        position: 'relative'
                                    }}
                                    onMouseEnter={() => setShowTooltip(true)}
                                    onMouseLeave={() => setShowTooltip(false)}
                                >
                                    <span>{arrow}</span>
                                    <span>
                                        {Math.abs(trendPercentage).toFixed(1)}%
                                    </span>
                                </div>
                                {showTooltip && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '100%',
                                        right: 0,
                                        marginTop: '0.5rem',
                                        padding: '0.75rem',
                                        backgroundColor: '#fff',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '0.5rem',
                                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                                        zIndex: 50,
                                        minWidth: '280px',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        <div style={{ marginBottom: '0.5rem', paddingBottom: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>
                                            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                                                Current Period
                                            </div>
                                            <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#111827', marginBottom: '0.125rem' }}>
                                                {formatValue(trendData.currentTotal)}
                                            </div>
                                            <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                                                {trendData.currentPeriod}
                                            </div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                                                Previous Period
                                            </div>
                                            <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#111827', marginBottom: '0.125rem' }}>
                                                {formatValue(trendData.previousTotal)}
                                            </div>
                                            <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                                                {trendData.previousPeriod}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })()}
                </div>
                {dataKeys.length > 1 && !stacked && !grouped && (
                    <div style={{ display: 'flex' }}>
                        {dataKeys.map(({ key, label }, index) => {
                            const isActive = activeChart === key;
                            return (
                                <button
                                    key={key}
                                    onClick={() => setActiveChart(key)}
                                    style={{
                                        position: 'relative',
                                        zIndex: 30,
                                        flex: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        gap: '0.25rem',
                                        borderTop: '1px solid hsl(var(--border))',
                                        borderLeft: index > 0 ? '1px solid hsl(var(--border))' : 'none',
                                        padding: '1rem 1.5rem',
                                        textAlign: 'left',
                                        backgroundColor: isActive ? 'hsl(var(--muted) / 0.5)' : 'transparent',
                                        cursor: 'pointer',
                                        transition: 'background-color 0.2s',
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!isActive) {
                                            e.currentTarget.style.backgroundColor = 'hsl(var(--muted) / 0.3)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!isActive) {
                                            e.currentTarget.style.backgroundColor = 'transparent';
                                        }
                                    }}
                                >
                                    <span style={{
                                        fontSize: '0.75rem',
                                        color: '#6b7280',
                                        lineHeight: '1rem'
                                    }}>
                                        {label}
                                    </span>
                                    <span style={{
                                        fontSize: '1.125rem',
                                        lineHeight: '1.5rem',
                                        fontWeight: 700,
                                        color: '#111827'
                                    }}>
                                        {totals[key]?.toLocaleString() || '0'}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                )}
                {/* {stacked && dataKeys.length > 1 && (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: '1rem',
                        padding: '0.75rem 1.5rem',
                        borderTop: '1px solid hsl(var(--border))',
                        backgroundColor: '#f9fafb'
                    }}>
                        {dataKeys.map(({ key, label, color }) => (
                            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <div style={{
                                    width: '12px',
                                    height: '12px',
                                    borderRadius: '2px',
                                    backgroundColor: color || `var(--color-${key})`
                                }} />
                                <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>{label}:</span>
                                <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#111827' }}>
                                    {totals[key]?.toLocaleString() || '0'}
                                </span>
                            </div>
                        ))}
                    </div>
                )} */}
            </CardHeader>
            <CardContent style={{
                padding: '1rem 1.5rem 1.5rem 1.5rem'
            }}>
                <div 
                    ref={chartContainerRef}
                    style={{
                        width: '100%',
                        height: `${height}px`,
                        touchAction: 'none',
                        position: 'relative'
                    }}
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                >
                    <ChartContainer
                        config={chartConfig}
                        style={{
                            width: '100%',
                            height: '100%'
                        }}
                    >
                        <BarChart
                            data={data}
                            margin={{
                                left: 0,
                                right: 0,
                                top: 5,
                                bottom: 5,
                            }}
                            onClick={(e) => {
                                // Handle chart click for mobile
                                if (e && e.activeTooltipIndex !== undefined) {
                                    if (isMobile) {
                                        setActiveIndex(e.activeTooltipIndex === activeIndex ? null : e.activeTooltipIndex);
                                    }
                                }
                            }}
                            onMouseMove={(e) => {
                                // On desktop, clear activeIndex to allow normal hover
                                if (!isMobile && activeIndex !== null) {
                                    setActiveIndex(null);
                                }
                            }}
                        >
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis
                                dataKey="date"
                                tickLine={false}
                                axisLine={false}
                                tickMargin={8}
                                minTickGap={32}
                                tick={{ fontSize: 12 }}
                                // height={50}
                                tickFormatter={(value) => {
                                    // Format date for display (e.g., "Apr 01" or "2024-04-01")
                                    if (value && typeof value === 'string') {
                                        // If it's already formatted like "Apr 01", return as is
                                        if (value.match(/^[A-Za-z]{3}\s\d{2}$/)) {
                                            return value;
                                        }
                                        // If it's a date string like "2024-04-01", format it
                                        if (value.match(/^\d{4}-\d{2}-\d{2}$/)) {
                                            const date = new Date(value);
                                            return date.toLocaleDateString('en-US', {
                                                month: 'short',
                                                day: 'numeric',
                                            });
                                        }
                                    }
                                    return value;
                                }}
                            />
                            <ChartTooltip
                                cursor={{ fill: 'rgba(0, 0, 0, 0.1)' }}
                                shared={false}
                                allowEscapeViewBox={{ x: false, y: true }}
                                position={{ y: 0 }}
                                wrapperStyle={{ pointerEvents: 'auto' }}
                                content={
                                    <ChartTooltipContent
                                        style={{
                                            backgroundColor: '#fff',
                                            border: '1px solid #e5e7eb',
                                            borderRadius: '0.5rem',
                                            padding: '0.75rem',
                                            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                                            minWidth: '160px',
                                            zIndex: 1000
                                        }}
                                        labelFormatter={(value, payload) => {
                                            // Get the date from the payload if available
                                            let dateValue = value;
                                            if (payload && payload.length > 0 && payload[0].payload) {
                                                const payloadData = payload[0].payload;
                                                dateValue = payloadData.date || payloadData.label || value;
                                            }

                                            // Format date for tooltip
                                            if (dateValue && typeof dateValue === 'string') {
                                                // If it's already formatted like "Apr 01", convert to full date
                                                if (dateValue.match(/^[A-Za-z]{3}\s\d{2}$/)) {
                                                    const currentYear = new Date().getFullYear();
                                                    const date = new Date(`${dateValue} ${currentYear}`);
                                                    return date.toLocaleDateString('en-US', {
                                                        month: 'short',
                                                        day: 'numeric',
                                                        year: 'numeric',
                                                    });
                                                }
                                                // If it's a date string like "2024-04-01", format it
                                                if (dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
                                                    const date = new Date(dateValue);
                                                    return date.toLocaleDateString('en-US', {
                                                        month: 'short',
                                                        day: 'numeric',
                                                        year: 'numeric',
                                                    });
                                                }
                                                // Try to parse as date
                                                const parsedDate = new Date(dateValue);
                                                if (!isNaN(parsedDate.getTime())) {
                                                    return parsedDate.toLocaleDateString('en-US', {
                                                        month: 'short',
                                                        day: 'numeric',
                                                        year: 'numeric',
                                                    });
                                                }
                                            }
                                            return dateValue || value;
                                        }}
                                        formatter={(value, name, item) => {
                                            // Format value based on the metric
                                            const isCurrency = name === 'totalRevenu' || name === 'totalRevenue';
                                            if (isCurrency && typeof value === 'number') {
                                                return [`AED ${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, dataKeys.find(dk => dk.key === name)?.label || name];
                                            }
                                            return [value?.toLocaleString() || value, dataKeys.find(dk => dk.key === name)?.label || name];
                                        }}
                                    />
                                }
                            />
                            {stacked ? (
                                // Stacked bars - show all dataKeys stacked
                                dataKeys.reverse().map(({ key }) => (
                                    <Bar
                                        key={key}
                                        dataKey={key}
                                        stackId="stack"
                                        fill={`var(--color-${key})`}
                                        radius={key === dataKeys[dataKeys.length - 1].key ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                                        style={{ cursor: 'pointer' }}
                                        onClick={(data, index) => {
                                            // if (isMobile) {
                                            //     setActiveIndex(activeIndex === index ? null : index);
                                            // }
                                        }}
                                    />
                                ))
                            ) : grouped ? (
                                // Grouped bars - all dataKeys rendered side by side per category
                                dataKeys.map(({ key }) => (
                                    <Bar
                                        key={key}
                                        dataKey={key}
                                        fill={`var(--color-${key})`}
                                        radius={[4, 4, 0, 0]}
                                        style={{ cursor: 'pointer' }}
                                    />
                                ))
                            ) : (
                                // Single bar - show only active chart
                                <Bar
                                    dataKey={activeChart}
                                    fill={`var(--color-${activeChart})`}
                                    radius={[4, 4, 0, 0]}
                                    style={{ cursor: 'pointer' }}
                                    onClick={(data, index) => {
                                        // if (isMobile) {
                                            // setActiveIndex(activeIndex === index ? null : index);
                                        // }
                                    }}
                                />
                            )}
                        </BarChart>
                    </ChartContainer>
                </div>
            </CardContent>
        </Card>
    );
};

// Memoize component to prevent unnecessary re-renders
export default React.memo(InteractiveBarChart, (prevProps, nextProps) => {
    return (
        prevProps.title === nextProps.title &&
        prevProps.description === nextProps.description &&
        prevProps.height === nextProps.height &&
        prevProps.stacked === nextProps.stacked &&
        prevProps.grouped === nextProps.grouped &&
        prevProps.invertTrend === nextProps.invertTrend &&
        prevProps.isCurrency === nextProps.isCurrency &&
        prevProps.valueSuffix === nextProps.valueSuffix &&
        JSON.stringify(prevProps.trendData) === JSON.stringify(nextProps.trendData) &&
        JSON.stringify(prevProps.data) === JSON.stringify(nextProps.data) &&
        JSON.stringify(prevProps.dataKeys) === JSON.stringify(nextProps.dataKeys)
    );
});

